import { useState, useRef, useEffect, forwardRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { getCachedImageUri, downloadAndCacheImage } from '../utils/imageCache';
import { debug } from '../utils/debug';

interface CachedImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string | null;
}

/**
 * CachedImage component that:
 * - On WEB: Renders a regular img tag (no caching, transparent pass-through)
 * - On NATIVE: 
 *   1. First tries to load from bundled assets (original src)
 *   2. On error (image not in bundle), checks cache
 *   3. If not cached and online, downloads and caches
 *   4. Updates src to cached version
 */
const CachedImage = forwardRef<HTMLImageElement, CachedImageProps>(
  ({ src, onError, onLoad, ...rest }, ref) => {
    // Native platform: use caching logic
    const [imageSrc, setImageSrc] = useState<string | null>(src);
    const [hasTriedCache, setHasTriedCache] = useState(false);
    const errorHandledRef = useRef(false);

    // Reset state when src changes
    useEffect(() => {
      setImageSrc(src);
      setHasTriedCache(false);
      errorHandledRef.current = false;
    }, [src]);

    // On web, just render a regular img tag - completely transparent
    if (!Capacitor.isNativePlatform()) {
      // Ensure src is a valid string (not null or undefined)
      if (!src || typeof src !== 'string') {
        debug.warn('[CachedImage] Invalid src on web:', src);
        return null;
      }
      
      return (
        <img
          ref={ref}
          src={src}
          onError={(e) => {
            debug.error('[CachedImage] Image failed to load on web:', src, e);
            if (onError) onError(e);
          }}
          onLoad={onLoad}
          loading="lazy"
          decoding="async"
          {...rest}
        />
      );
    }

    const handleError = async (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      // Prevent multiple error handlers from firing
      if (errorHandledRef.current || !src) {
        if (onError) {
          onError(e);
        }
        return;
      }

      errorHandledRef.current = true;
      debug.log('[CachedImage] Bundled image failed to load, trying cache:', src);

      // First, try to get from cache
      if (!hasTriedCache) {
        setHasTriedCache(true);
        const cachedUri = await getCachedImageUri(src);
        
        if (cachedUri) {
          debug.log('[CachedImage] Using cached image:', src);
          setImageSrc(cachedUri);
          return; // Successfully loaded from cache
        }
      }

      // Not in cache, try to download (if online)
      if (navigator.onLine) {
        debug.log('[CachedImage] Image not in cache, downloading:', src);
        const downloadedUri = await downloadAndCacheImage(src);
        
        if (downloadedUri) {
          debug.log('[CachedImage] Downloaded and cached image:', src);
          setImageSrc(downloadedUri);
          return; // Successfully downloaded and cached
        }
      }

      // All attempts failed, call original error handler
      debug.warn('[CachedImage] Failed to load image from bundle, cache, or download:', src);
      if (onError) {
        onError(e);
      }
    };

    if (!imageSrc) {
      return null;
    }

    return (
      <img
        ref={ref}
        src={imageSrc}
        onError={handleError}
        onLoad={onLoad}
        loading="lazy"
        decoding="async"
        {...rest}
      />
    );
  }
);

CachedImage.displayName = 'CachedImage';

export default CachedImage;

